import api from './api';
import type { PaymentOrderCouponInfo } from './paymentService';

export type FinanceReportTab =
  | 'overview'
  | 'order'
  | 'aftersale'
  | 'withdraw'
  | 'frozen'
  | 'service'
  | 'deposit'
  | 'providerSettlement'
  | 'providerIncome';

export interface FinanceReportSummary {
  miniProgramIncome: number;
  miniProgramFulfilledCount: number;
  miniProgramPaymentCount: number;
  corporateIncome: number;
  corporateFulfilledCount: number;
  corporatePaymentCount: number;
  orderIncome: number;
  orderExpense: number;
  fulfilledOrderAmount: number;
  verifiedCouponAmount: number;
  wechatIncome: number;
  wechatPaymentCount: number;
  meituanIncome: number;
  meituanAllocatedIncome: number;
  meituanFulfilledCount: number;
  meituanVerifiedGrossIncome: number;
  meituanVerifiedIncome: number;
  meituanAllocatedVerifiedIncome: number;
  meituanStatementMarketingFee: number;
  meituanReconciledIncome: number;
  meituanStatementLoaded: boolean;
  meituanStatementOrderPaidAmount: number;
  meituanStatementPlatformSubsidy: number;
  meituanStatementSettlementAmount: number;
  meituanCouponMeituanPayAmount: number;
  meituanCouponVoucherAmount: number;
  meituanCouponMerchantVoucherAmount: number;
  meituanCouponMerchantFundedAmount: number;
  meituanCouponPlatformFundedAmount: number;
  meituanPaymentShareDetailLoaded: boolean;
  meituanPaymentShareMatchedCoupons: number;
  meituanPaymentShareExpectedCoupons: number;
  meituanPaymentCompositionLoaded: boolean;
  meituanSnapshotOrderTotalAmount: number;
  meituanSnapshotPaidAmount: number;
  meituanSnapshotPayDiscountAmount: number;
  meituanSnapshotMerchantAmount: number;
  meituanSnapshotPlatformAmount: number;
  meituanOrderReferenceTotalAmount: number;
  meituanOrderReferencePaidAmount: number;
  meituanOrderReferencePayDiscount: number;
  meituanOrderReferenceMerchantAmount: number;
  meituanOrderReferencePlatformAmount: number;
  meituanPaymentSnapshotLoaded: boolean;
  meituanPaymentSnapshotMatchedCount: number;
  meituanPaymentSnapshotExpectedCount: number;
  meituanCompletedOnlyIncome: number;
  meituanMerchantMarketingFee: number;
  meituanPlatformServiceFee: number;
  meituanVerifiedOrderCount: number;
  meituanCompletedOnlyOrderCount: number;
  meituanUnverifiedCouponCount: number;
  meituanPartialVerifiedCouponCount: number;
  meituanPartialUnverifiedCouponCount: number;
  meituanFullyVerifiedOrderCount: number;
  meituanPartiallyVerifiedOrderCount: number;
  meituanUnverifiedOrderCount: number;
  meituanCouponUnknownOrderCount: number;
  douyinIncome: number;
  douyinFulfilledCount: number;
  aftersaleAmount: number;
  withdrawAmount: number;
  frozenAmount: number;
  serviceFlowAmount: number;
  depositAmount: number;
  providerSettlementAmount: number;
  providerIncomeAmount: number;
  netAmount: number;
  transactionCount: number;
  fulfilledOrderCount: number;
  verifiedCouponCount: number;
}

export interface FinanceTrendItem {
  date: string;
  orderIncome: number;
  orderExpense: number;
  aftersaleAmount: number;
  withdrawAmount: number;
  frozenAmount: number;
  serviceFlowAmount: number;
  depositAmount: number;
  providerSettlementAmount: number;
  providerIncomeAmount: number;
}

export interface FinanceFlowRecord {
  id: string;
  flowNo: string;
  type: Exclude<FinanceReportTab, 'overview'>;
  title: string;
  amount: number;
  direction: 'income' | 'expense' | 'freeze';
  status: string;
  relatedOrderNo: string;
  serviceOrderId: string;
  orderSource: string;
  creationMode?: string;
  orderAmount: number;
  meituanReservationNo: string;
  meituanOrderNo: string;
  settlementType: string;
  settlementRatio: number;
  settlementAmountType: string;
  estimatedAmount: number;
  providerName: string;
  subjectName: string;
  subjectPhone: string;
  customerName: string;
  customerPhone: string;
  serviceDate: string;
  settlementDate: string;
  incomeAt: string;
  occurredAt: string;
  remark: string;
  paymentOrderNo: string;
  couponInfo?: PaymentOrderCouponInfo[];
  /** Imported Meituan statement fee, summed across this unified order's matched coupons. */
  meituanStatementMarketingFee?: number;
  meituanMerchantMarketingAmount?: number;
  meituanAddPriceDetail?: string;
  tradeNo: string;
  payChannel: string;
  payType: string;
  payScene: string;
  bizType: string;
  appCode: string;
  productName: string;
  categoryName: string;
  petName: string;
  petQuantity: number;
  addressText: string;
  serviceTimeDesc: string;
  totalDays: number;
  unitPrice: number;
  totalAmount: number;
  orderStatus: string;
  payStatus: string;
  payMethod: string;
  dispatchMode: string;
  assignedStaffName: string;
  assignedStaffPhone: string;
  orderCreatedAt: string;
  orderUpdatedAt: string;
  fulfillmentCompletedAt: string;
  meituanVerifiedAt: string;
  meituanVerifiedCode: string;
  meituanVerifiedAmount: number;
  meituanVerifiedGrossAmount?: number;
  meituanMerchantFundedAmount?: number;
  meituanVerifiedAmountSource?: string;
  meituanVerifiedCoverage?: string;
  meituanIncomeAmountSource?: string;
  meituanOrderPaidAmount?: number;
  meituanPayDiscountAmount?: number;
  meituanPlatformServiceFee?: number;
  meituanPlatformSubsidy?: number;
  meituanSettlementAmount?: number;
  withdrawMethod: string;
  withdrawAccountName: string;
  withdrawAccountNo: string;
}

export interface FinanceReportData {
  summary: FinanceReportSummary;
  trend: FinanceTrendItem[];
  records: FinanceFlowRecord[];
  recordsTotal?: number;
  recordsTruncated?: boolean;
}

export interface FinanceReportExportConfig {
  reportKey: string;
  fieldKeys: string[];
  defaultLimit: number;
  columnSizes?: Record<string, { width?: number; height?: number }>;
}

export interface MeituanSettlementImportEntry {
  statementDate: string;
  incomeType: string;
  externalOrderNo: string;
  couponCode: string;
  verifiedAt?: string;
  orderTotalAmount: number;
  orderPaidAmount: number;
  merchantMarketingFee: number;
  platformServiceFee: number;
  platformSubsidy: number;
  otherAdjustment: number;
  channelCommission: number;
  settlementAmount: number;
  payoutDate?: string;
  payoutStatus?: string;
  payoutNo?: string;
}

export interface EditableMeituanSettlementLedgerEntry extends MeituanSettlementImportEntry {
  id: string;
  updatedAt: string;
  status: 'active' | 'voided';
  schemaVersion: number;
  changeReason: string;
  sourceRowNo: number;
}

export interface EditableMeituanSettlementImportEntry extends MeituanSettlementImportEntry {
  action: '新增' | '更新' | '作废';
  ledgerRowId: string;
  rowVersion: number;
  providedFields: string[];
  changeReason: string;
  sourceRowNo: number;
}

export interface MeituanFinancialHealth {
  checkedAt: string;
  ready: boolean;
  schema: Array<{
    table: string;
    exists: boolean;
    missingColumns?: string[];
    missingIndexes?: string[];
    checkError?: string;
    ready: boolean;
  }>;
  syncTasks: {
    statusCounts: Record<string, number>;
    staleProcessingCount: number;
    lastCompletedAt?: string;
    lastError?: string;
  };
  recommendations: string[];
}

const typeTitleMap: Record<FinanceFlowRecord['type'], string> = {
  order: '订单收入',
  aftersale: '售后流水',
  withdraw: '提现流水',
  frozen: '服务流水已结算',
  service: '服务流水未结算',
  deposit: '保证金流水',
  providerSettlement: '代理商结算流水',
  providerIncome: '代理商收入流水',
};

export const getFinanceReport = async (params: {
  startDate: string;
  endDate: string;
  fulfillmentStartDate?: string;
  fulfillmentEndDate?: string;
  paymentStartDate?: string;
  paymentEndDate?: string;
  verifiedStartDate?: string;
  verifiedEndDate?: string;
  verifiedStatus?: 'verified' | 'unverified';
  tab?: FinanceReportTab;
  customerName?: string;
  customerPhone?: string;
  orderNo?: string;
  orderSource?: string;
  creationModeGroup?: 'manual' | 'automatic' | 'unknown';
  couponCode?: string;
  meituanReservationNo?: string;
  meituanOrderNo?: string;
  providerName?: string;
  providerPhone?: string;
  title?: string;
  status?: string;
}): Promise<FinanceReportData> => {
  const response = await api.post<FinanceReportData>('/finance/report', params, { timeout: 30000 });
  return response.data;
};

export const importMeituanSettlementEntries = async (entries: MeituanSettlementImportEntry[]): Promise<{ imported: number }> => {
  const response = await api.post<{ imported: number }>('/finance/report/meituan-settlement/import', { entries }, { timeout: 60000 });
  return response.data;
};

export const getEditableMeituanSettlementLedger = async (): Promise<{
  schemaVersion: number;
  entries: EditableMeituanSettlementLedgerEntry[];
}> => {
  const response = await api.get<{ schemaVersion: number; entries: EditableMeituanSettlementLedgerEntry[] }>('/finance/report/meituan-settlement/editable-ledger', { timeout: 30000 });
  return response.data;
};

export const importEditableMeituanSettlementLedger = async (entries: EditableMeituanSettlementImportEntry[]): Promise<{ imported: number }> => {
  const response = await api.post<{ imported: number }>('/finance/report/meituan-settlement/editable-ledger/import', {
    schemaVersion: 2,
    entries,
  }, { timeout: 60000 });
  return response.data;
};

export const getMeituanFinancialHealth = async (): Promise<MeituanFinancialHealth> => {
  const response = await api.get<MeituanFinancialHealth>('/finance/report/meituan-financial/health', { timeout: 30000 });
  return response.data;
};

export const getFinanceReportExportConfig = async (reportKey: string): Promise<FinanceReportExportConfig> => {
  const response = await api.get<FinanceReportExportConfig>('/finance/report/export-config', {
    params: { reportKey },
  });
  return response.data;
};

export const saveFinanceReportExportConfig = async (
  data: FinanceReportExportConfig,
): Promise<FinanceReportExportConfig> => {
  const response = await api.put<FinanceReportExportConfig>('/finance/report/export-config', data);
  return response.data;
};

export const financeFlowTypeText = (type: FinanceFlowRecord['type']) => typeTitleMap[type] || type;

